import { defineStore } from "pinia"
import { apiClient } from "@/services/api"
import { useNotificationStore } from "@/store/notification"
import { getViolationMessage } from "@/services/violations"
import { AbsenceModel } from "@/components/shared/personnelplanning/dialogs/absence-assign/AssignAbsenceDialog.vue"
import {
  removeTimelineAssignment,
  updateProjectSpanLocal,
  updateTimelineProjectAssignment,
  updateTimelineSpan,
} from "@/store/calendar-utils"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import {
  averageCapacityOn,
  type CapacitySpan,
  expandRangesToDayMap,
  mergeDayMapToRanges,
  toCapacitySpans,
} from "@/components/shared/personnelplanning/gantt/capacity/capacityRanges"

/**
 * The built-in calendar that shows all projects. Every tenant has exactly one under this id; the backend
 * stores it the first time it is opened, so a link to it works before it exists. It has its own place
 * above the lists, and cannot be deleted, renamed or pinned. See docs/personnel-planning-detail.md.
 */
export const ALL_PROJECTS_CALENDAR_ID = "all-projects"

const matches = (query: string, ...texts: Array<string | null | undefined>) =>
  texts.some(text => (text ?? "").toLowerCase().includes(query))

/** A project row matches by name or project number; its phases come along. */
const projectMatches = (project: any, query: string) => matches(query, project.name, project.alternativeId)

/** A person matches by name or position; a team by its name or any member's. */
const employeeMatches = (employee: any, query: string) =>
  employee.type === "MULTIPLE"
    ? matches(query, employee.name) ||
      (employee.assignedEmployees ?? []).some((member: any) =>
        matches(query, `${member.firstName} ${member.lastName}`, member.position?.name)
      )
    : matches(query, `${employee.firstName} ${employee.lastName}`, employee.position?.name)

let lastPage = -1
let reconcileTimer: ReturnType<typeof setTimeout> | null = null

/**
 * The board's own writes, for the background reconcile: how many were sent (only ever grows) and how
 * many the server has not answered yet. A reconcile read that overlaps one of them may be answered
 * from the state before it, and laying that over the board puts what was just dropped back where it
 * came from — until the write's own reconcile, seconds later, moves it again.
 */
const boardWrites = { sent: 0, open: 0 }

/** Sends one of the board's writes, counted for the reconcile (see `boardWrites`). */
function trackWrite<T>(request: Promise<T>): Promise<T> {
  boardWrites.sent++
  boardWrites.open++
  return request.finally(() => boardWrites.open--)
}

/**
 * In the per-view walkthrough's frame the board keeps what was dragged, and asks nobody.
 *
 * The four mutations below already patch the board locally; the request is only what makes the patch
 * allowed, and the background reconcile is what checks it later. Inside the frame neither can happen:
 * the sandbox refuses every write (`services/viewHelp/sandbox.ts`), which would send the mutation down
 * its error path — a full refetch of the sample calendar — and a span the reader had just watched the
 * pointer drag would snap back to where it started and read as the product being broken. The
 * reconcile would do the same 1.5 seconds later even if the write had gone through.
 *
 * So in the frame the local patch *is* the whole mutation. Nothing reaches the network, and the
 * sample calendar is a copy per frame load that is thrown away with it. The same shape as the
 * time-tracking store's demo clock (`helpDemo`), and for the same reason: some things cannot be
 * demonstrated by answering a read. See docs/view-help.md.
 */
const KEEPS_WHAT_WAS_DONE = IS_VIEW_HELP_EMBED

const showNotification = error => {
  if (error.response?.status === 400) {
    const message = getViolationMessage(error.response.data)
    const notificationStore = useNotificationStore()
    notificationStore.raiseError({
      status: error.response.status,
      message: message,
    })
  }
}

export const useCalendarStore = defineStore("calendar", {
  state: () => ({
    data: {
      calendarId: "",
      name: "",
      // Every project instead of picked ones; the board then offers no adding or removing of projects
      includesAllProjects: false,
      projects: { resources: [] },
      employees: { resources: [] },
    } as any,
    pinnedCalendars: [] as any[],
    unpinnedCalendars: [] as any[],
    /**
     * The key figures of the built-in calendar of all projects, for the banner above the lists
     * (`FrontendAllProjectsOverviewResponse`); `null` until loaded, or when that failed.
     */
    allProjectsOverview: null as null | {
      runningProjects: number
      upcomingProjects: number
      plannedToday: number
      neededToday: number
      understaffedDays: number
      horizonDays: number
    },
    /**
     * The board's two search fields (Projekte, Mitarbeiter lanes). They narrow what the lanes show, and
     * the gantt numbers its rows by what it shows — so every lookup from a row number goes through
     * `getVisibleProjects` / `getVisibleEmployees`, never through `data.*.resources`. Cleared when
     * another calendar is loaded.
     */
    boardFilter: { projects: "", employees: "" },
  }),
  getters: {
    getData: state => state.data,
    getCalendars: state => [...state.pinnedCalendars, ...state.unpinnedCalendars],
    getPinnedCalendars: state => state.pinnedCalendars,
    getUnpinnedCalendars: state => state.unpinnedCalendars,
    /** The project rows the board shows: all of them, or those the Projekte search matches. */
    getVisibleProjects: (state): any[] => {
      const all = state.data.projects?.resources ?? []
      const query = state.boardFilter.projects.trim().toLowerCase()
      return query ? all.filter((project: any) => projectMatches(project, query)) : all
    },
    /** The employee rows the board shows: all of them, or those the Mitarbeiter search matches. */
    getVisibleEmployees: (state): any[] => {
      const all = state.data.employees?.resources ?? []
      const query = state.boardFilter.employees.trim().toLowerCase()
      return query ? all.filter((employee: any) => employeeMatches(employee, query)) : all
    },
    /**
     * The built-in calendar of all projects keeps its capacity lane a summary — "Geplant" against
     * "Personal", nothing per phase — until the Projekte search leaves exactly one project. The lane,
     * its section bar and the phase menus all read this, so they cannot disagree.
     */
    isCapacitySummary(): boolean {
      return !!this.data.includesAllProjects && this.getVisibleProjects.length !== 1
    },
  },
  actions: {
    // Catches like fetchPagedUnpinned below, so a failed load cannot reject into the caller's mounted hook.
    async fetchAllPinned({ sortBy, searchValue, filterByPresent, filterByFuture, filterByPast }) {
      return apiClient
        .get("/personnel-planning/pinned", {
          params: {
            sortBy: sortBy,
            search: searchValue,
            filterByPresent: filterByPresent,
            filterByFuture: filterByFuture,
            filterByPast: filterByPast,
          },
        })
        .then(res => {
          if (res.status === 200) {
            this.pinnedCalendars = res?.data
          }
        })
        .catch(showNotification)
    },
    async fetchPagedUnpinned({
      page = 0,
      itemsPerPage = 10,
      sortBy = null,
      searchValue = null,
      filterByPresent = null,
      filterByFuture = null,
      filterByPast = null,
    }) {
      return apiClient
        .get("/personnel-planning", {
          params: {
            page: page,
            itemsPerPage: itemsPerPage,
            sortBy: sortBy,
            search: searchValue,
            filterByPresent: filterByPresent,
            filterByFuture: filterByFuture,
            filterByPast: filterByPast,
          },
        })
        .then(res => {
          if (res.status === 200) {
            const data = res?.data?.content ?? []
            if (page === 0) {
              this.unpinnedCalendars = [...data]
            } else if (lastPage === page) {
              this.unpinnedCalendars = [...data]
            } else if (lastPage === page - 1) {
              data.forEach((calendar: any) => this.unpinnedCalendars.push(calendar))
            } else {
              this.unpinnedCalendars = [...data]
            }
            lastPage = page
          }
          return res
        })
        .catch(error => {
          showNotification(error)
        })
    },
    /**
     * The key figures of the built-in calendar of all projects, for the banner above the lists (which
     * leave that calendar out). Silent: without them the banner shows no figures but still opens the
     * calendar — and the Erklärung's sandbox answers no such read.
     */
    async fetchAllProjectsOverview() {
      return apiClient
        .get("/personnel-planning/all-projects-overview")
        .then(res => {
          this.allProjectsOverview = res.data ?? null
        })
        .catch(() => {
          this.allProjectsOverview = null
        })
    },
    /**
     * The calendars one project or one employee is planned in (`{ id, name }`, sorted by name),
     * **returned** rather than stored: the record's page asks for it to label its actions menu, and
     * writing it into the calendar lists would leave the overview showing only these on the next visit.
     *
     * Silent and never rejecting: a failure costs the menu its "öffnen" entry — not worth a snackbar
     * over a page that loaded fine.
     */
    async fetchCalendarsOf(kind: "project" | "employee", id: string): Promise<{ id: string; name: string }[]> {
      return apiClient
        .get(`/personnel-planning/${kind}/${id}`)
        .then(res => (Array.isArray(res.data) ? res.data : []))
        .catch(() => [])
    },
    /**
     * Create a new calendar.
     * @return calendarId of the newly created calendar.
     */
    async createCalendar(data: { name: string; projectIds: string[] }) {
      return apiClient
        .post("/personnel-planning", data)
        .then(res => {
          return res.data.calendarId
        })
        .catch(error => {
          showNotification(error)
        })
    },
    async pinCalendar(calendarId: string, pinned: boolean) {
      const calendar = !pinned
        ? this.pinnedCalendars.find(c => c.id === calendarId)
        : this.unpinnedCalendars.find(c => c.id === calendarId)

      const apply = () => {
        calendar.pinned = pinned
        if (pinned) {
          this.unpinnedCalendars = this.unpinnedCalendars.filter(c => c.id !== calendarId)
          this.pinnedCalendars.push(calendar)
        } else {
          this.pinnedCalendars = this.pinnedCalendars.filter(c => c.id !== calendarId)
          this.unpinnedCalendars.push(calendar)
        }
      }
      // In the Erklärung's frame the pin the walkthrough's drag changes is kept locally, as the board's
      // own mutations are (KEEPS_WHAT_WAS_DONE): the sandbox refuses the write.
      if (KEEPS_WHAT_WAS_DONE) {
        if (calendar) apply()
        return
      }

      return apiClient
        .put(`/personnel-planning/pin`, { calendarId, pinned })
        .then(apply)
        .catch(error => {
          showNotification(error)
        })
    },
    /**
     * Fetches the calendar detail data. Pass an explicit date window to load a
     * different span than the default (project span plus rendered buffers) —
     * the backend limits assignments and capacity to the requested window.
     * `reconcile` marks the background reconcile (see `scheduleReconcile`), whose answer may be dropped.
     */
    async fetchData(calendarId: string, window?: { from: string; to: string }, { reconcile = false } = {}) {
      // A fresh full fetch supersedes any pending background reconcile
      if (reconcileTimer) {
        clearTimeout(reconcileTimer)
        reconcileTimer = null
      }
      const writesSent = boardWrites.sent
      return apiClient(`/personnel-planning/${calendarId}`, {
        params: window ? { from: window.from, to: window.to } : undefined,
      })
        .then(res => {
          // A write sent meanwhile may be missing from the answer; it schedules a reconcile of its own
          if (reconcile && boardWrites.sent !== writesSent) return
          if (res.status === 200) {
            // A search typed on one calendar means nothing on the next
            if (res.data?.calendarId !== this.data.calendarId) this.boardFilter = { projects: "", employees: "" }
            this.data = res.data
          }
        })
        .catch(error => {
          showNotification(error)
        })
    },
    /**
     * Schedules a single debounced background refetch to reconcile locally
     * patched state (e.g. capacity maps) with the server. Mutations that are
     * patched optimistically use this instead of an immediate full refetch,
     * so rapid interactions (dragging spans) collapse into one request.
     * A reconcile never overlaps a write of the board (see `boardWrites`): it waits for open writes,
     * and drops its answer when one was sent while it was underway.
     */
    scheduleReconcile(delayMs = 1500) {
      // Nothing to reconcile with in the frame — and the refetch would undo the demonstration.
      if (KEEPS_WHAT_WAS_DONE) return
      if (reconcileTimer) clearTimeout(reconcileTimer)
      const calendarId = this.data.calendarId
      reconcileTimer = setTimeout(() => {
        reconcileTimer = null
        // An open write schedules a reconcile of its own once it is answered
        if (boardWrites.open > 0) return
        // Skip if the user navigated to a different calendar in the meantime
        if (calendarId && this.data.calendarId === calendarId) {
          this.fetchData(calendarId, undefined, { reconcile: true })
        }
      }, delayMs)
    },
    // adds an employee into a team
    async assignEmployeeToTeam(calendarId: string, employeeId: string, teamId: string) {
      return apiClient
        .post("/personnel-planning/assign-employee-to-team", { calendarId, employeeId, teamId })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
    },
    // adds a project to a calendar
    async addProjectsToCalendar(calendarId: string, projectIds: string[]) {
      return apiClient
        .post("/personnel-planning/add-projects-to-calendar", { calendarId, projectIds })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
    },
    // Removes a project from a calendar
    async removeProjectsFromCalendar(calendarId: string, projectIds: string[]) {
      await apiClient
        .post("/personnel-planning/remove-projects-from-calendar", { calendarId, projectIds })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
      // this.data.projects.resources = this.data.projects.resources.filter(p => !projectIds.includes(p.id))
    },
    // adds an employee/team to a calendar
    async addEmployeesToCalendar(calendarId: string, employeeIds: string[], teamIds: string[]) {
      return apiClient
        .post("/personnel-planning/add-employees-to-calendar", { calendarId, employeeIds, teamIds })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
    },
    // Removes an employee/team from a calendar
    async removeEmployeesFromCalendar(calendarId: string, employeeIds: string[], teamIds: string[]) {
      return apiClient
        .post("/personnel-planning/remove-employees-from-calendar", { calendarId, employeeIds, teamIds })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
    },
    /**
     * Assigns Employee or Team to a project
     * @returns Array of timelineIds assigned to the project.
     */
    async assignEmployeeToProject(
      calendarId: string,
      timelineId: string | null,
      employeeIds: string[] | null,
      teamIds: string[] | null,
      projectId: string,
      phaseId: string | null,
      startDate: string,
      endDate: string
    ) {
      // Dropping people onto a project row assigns them there and then, with no form in between — so
      // in the frame this is one of the mutations that has to keep what the pointer did. The caller
      // writes the assignment into the board itself and only uses the answer to carry the ids the
      // server would have minted; an empty list leaves it the ones it generated. See
      // KEEPS_WHAT_WAS_DONE.
      if (KEEPS_WHAT_WAS_DONE) return []
      return apiClient
        .post("/personnel-planning/assign", {
          calendarId: calendarId,
          assignmentId: timelineId,
          employeeIds: employeeIds,
          teamIds: teamIds,
          projectId: projectId,
          phaseId: phaseId,
          startDate: startDate,
          endDate: endDate,
          type: phaseId ? "phase" : "project",
        })
        .then(res => {
          this.fetchData(this.getData.calendarId)
          return res
        })
        .then(res => res.data)
        .catch(error => {
          showNotification(error)
        })
    },
    async assignAbsence(
      calendarId: string,
      employeeIds: string[],
      teamIds: string[],
      absence: AbsenceModel,
      timelineId: string | null,
      startDate: string,
      endDate: string
    ) {
      await apiClient
        .post("/personnel-planning/assign", {
          calendarId: calendarId,
          assignmentId: timelineId,
          employeeIds: employeeIds,
          teamIds: teamIds,
          startDate: startDate,
          endDate: endDate,
          absenceId: absence.type.id,
          span: absence.span,
          fromTime: absence.fromTime,
          toTime: absence.toTime,
          type: "absence",
        })
        .then(() => this.fetchData(this.getData.calendarId))
        .then(res => res.data)
        .catch(error => {
          showNotification(error)
        })
    },
    /**
     * Sets the planned capacity of one phase for the given days (`{ "YYYY-MM-DD": persons }`);
     * `null` for a day removes its explicit value, so it falls back to the phase's average.
     * Days outside the phase are ignored, as the backend ignores them. The phase's capacity and
     * the summed "Geplant" line are patched locally first, so a drag in the capacity chart stays
     * where it was dropped. A failed request reloads the calendar rather than restoring a snapshot:
     * a snapshot taken before this request would also undo a later edit that did go through.
     * @returns whether the capacity was stored
     */
    async assignCapacity(projectId: string, phaseId: string, valuesByDate: Record<string, number | null>) {
      return this.assignCapacities(projectId, { [phaseId]: valuesByDate })
    },
    /**
     * `assignCapacity` for several phases of one project at once (`{ phaseId: { day: persons } }`):
     * a drag on "Geplant" that crosses from one phase into the next. Every phase is patched locally
     * first; the requests go one after another, because each is a read-modify-write of the whole
     * project and two at once would lose one of them.
     * @returns whether the capacity was stored
     */
    async assignCapacities(projectId: string, valuesByPhase: Record<string, Record<string, number | null>>) {
      const project = this.data.projects.resources.find((p: any) => p.id === projectId)
      const expected = expandRangesToDayMap(this.data.expectedCapacity)
      const requests: Array<{ projectId: string; phaseId: string; spans: CapacitySpan[] }> = []

      for (const [phaseId, valuesByDate] of Object.entries(valuesByPhase)) {
        const phase = project?.phases?.find((p: any) => p.id === phaseId)
        if (!phase) continue

        const inPhase = Object.fromEntries(
          Object.entries(valuesByDate).filter(([date]) => date >= phase.start && date <= phase.end)
        )
        const spans = toCapacitySpans(inPhase)
        if (spans.length === 0) continue

        const phaseCapacity = expandRangesToDayMap(phase.capacity)
        for (const [date, value] of Object.entries(inPhase)) {
          const effective = value ?? averageCapacityOn(date, project.workDays, phase.averageCapacity)
          expected[date] = (expected[date] ?? 0) - (phaseCapacity[date] ?? 0) + effective
          phaseCapacity[date] = effective
        }
        phase.capacity = mergeDayMapToRanges(phaseCapacity)
        requests.push({ projectId, phaseId, spans })
      }
      if (requests.length === 0) return false
      this.data.expectedCapacity = mergeDayMapToRanges(expected)

      // The local patch is the whole mutation in the frame. See KEEPS_WHAT_WAS_DONE.
      if (KEEPS_WHAT_WAS_DONE) return true
      const sendInTurn = async () => {
        for (const request of requests) await apiClient.post("/project/assign-capacity", request)
      }
      return trackWrite(sendInTurn())
        .then(() => {
          this.scheduleReconcile()
          return true
        })
        .catch(error => {
          showNotification(error)
          this.fetchData(this.data.calendarId)
          return false
        })
    },
    /**
     * Updates the start/end date of a project or a phase. Patched locally first, so a dragged bar
     * stays where it was dropped; a failed request reloads the calendar (see assignCapacity).
     */
    async updateProjectSpan(
      calendarId: string,
      projectId: string | number,
      phaseId: string | number | null,
      startDate: string,
      endDate: string
    ) {
      updateProjectSpanLocal(this.data.projects, projectId, phaseId, startDate, endDate)
      if (KEEPS_WHAT_WAS_DONE) return
      return trackWrite(
        apiClient.put("/personnel-planning/update-project-span", {
          calendarId: calendarId,
          projectId: projectId,
          phaseId: phaseId,
          startDate: startDate,
          endDate: endDate,
        })
      )
        .then(() => {
          // A debounced background fetch reconciles derived data (capacity)
          this.scheduleReconcile()
        })
        .catch(error => {
          showNotification(error)
          this.fetchData(this.getData.calendarId)
        })
    },
    /**
     * Updates the start/end date of an assigned employee/member/team. Patched locally first, like
     * updateProjectSpan.
     */
    async updateEmployeeSpan(
      calendarId: string,
      timelineId: string,
      employeeId: string | number | null,
      teamId: string | number | null,
      startDate: string,
      endDate: string
    ) {
      updateTimelineSpan(this.data.employees?.resources || [], timelineId, employeeId, teamId, startDate, endDate)
      if (KEEPS_WHAT_WAS_DONE) return
      return trackWrite(
        apiClient.put("/personnel-planning/update-employee-span", {
          calendarId: calendarId,
          assignmentId: timelineId,
          employeeId: employeeId,
          teamId: teamId,
          startDate: startDate,
          endDate: endDate,
        })
      )
        .then(() => {
          this.scheduleReconcile()
        })
        .catch(error => {
          showNotification(error)
          this.fetchData(this.getData.calendarId)
        })
    },
    /**
     * Removes an assigned employee/member/team timeline. Patched locally first, like updateProjectSpan.
     */
    async removeEmployeeAssignment(
      calendarId: string,
      timespanId: string,
      employeeId: string | number | null,
      teamId: string | number | null
    ) {
      removeTimelineAssignment(this.data.employees?.resources || [], timespanId, employeeId, teamId)
      if (KEEPS_WHAT_WAS_DONE) return
      return trackWrite(
        apiClient.post("/personnel-planning/remove-assignment", {
          calendarId: calendarId,
          timespanId: timespanId,
          employeeId: employeeId,
          teamId: teamId,
        })
      )
        .then(() => {
          this.scheduleReconcile()
        })
        .catch(error => {
          showNotification(error)
          this.fetchData(this.getData.calendarId)
        })
    },
    /**
     * Changes the assigned project/phase of an employee/member/team timeline. Patched locally first,
     * like updateProjectSpan; the dates stay, as on the backend.
     */
    async changeAssignedProject(
      calendarId: string,
      timespanId: string,
      employeeId: string | number | null,
      teamId: string | number | null,
      projectId: string | number,
      phaseId: string | number | null
    ) {
      updateTimelineProjectAssignment(
        this.data.employees?.resources || [],
        this.data.projects?.resources || [],
        timespanId,
        employeeId,
        teamId,
        projectId,
        phaseId
      )
      if (KEEPS_WHAT_WAS_DONE) return
      return trackWrite(
        apiClient.put("/personnel-planning/change-assigned-project", {
          calendarId: calendarId,
          assignmentId: timespanId,
          employeeId: employeeId,
          teamId: teamId,
          projectId: projectId,
          phaseId: phaseId,
        })
      )
        .then(() => {
          this.scheduleReconcile()
        })
        .catch(error => {
          showNotification(error)
          this.fetchData(this.getData.calendarId)
        })
    },
    /**
     * Updates employee information in the calendar data
     */
    async updateEmployeeInCalendar(
      employeeId: string,
      updates: { firstName?: string; lastName?: string; position?: { name: string } | null }
    ) {
      const employees = this.data.employees?.resources || []

      // Update in SINGLE employees
      const singleEmployee = employees.find((emp: any) => emp.type === "SINGLE" && emp.id === employeeId)
      if (singleEmployee) {
        if (updates.firstName !== undefined) singleEmployee.firstName = updates.firstName
        if (updates.lastName !== undefined) singleEmployee.lastName = updates.lastName
        if (updates.position !== undefined) singleEmployee.position = updates.position
      }

      // Update in MULTIPLE employees (teams)
      employees
        .filter((emp: any) => emp.type === "MULTIPLE")
        .forEach((team: any) => {
          const member = team.assignedEmployees?.find((member: any) => member.id === employeeId)
          if (member) {
            if (updates.firstName !== undefined) member.firstName = updates.firstName
            if (updates.lastName !== undefined) member.lastName = updates.lastName
            if (updates.position !== undefined) member.position = updates.position
          }
        })
    },
    async dissolveTeam(calendarId: string, teamId: string) {
      return apiClient
        .post("/personnel-planning/dissolve-team", { calendarId, teamId })
        .then(() => this.fetchData(this.getData.calendarId))
        .catch(error => {
          showNotification(error)
        })
    },
    async addProjectPhase(projectId: string, phase: any) {
      this.data.projects.resources.find((p: any) => p.id === projectId).phases.push(phase)
      this.fetchData(this.getData.calendarId)
    },
    /**
     * Renames a calendar
     */
    async renameCalendar(calendarId: string, newName: string) {
      await apiClient.put(`/personnel-planning/rename`, { calendarId, name: newName })
      // Update local state
      this.data.name = newName
      const calendar = this.getCalendars.find((c: any) => c.id === calendarId)
      if (calendar) {
        calendar.name = newName
      }
    },
    /**
     * Deletes a calendar
     */
    async deleteCalendar(calendarId: string) {
      await apiClient.delete(`/personnel-planning/${calendarId}`)
      // Update local state
      this.pinnedCalendars = this.pinnedCalendars.filter((c: any) => c.id !== calendarId)
      this.unpinnedCalendars = this.unpinnedCalendars.filter((c: any) => c.id !== calendarId)
    },
    async updateProjectPhase(projectId: string, phaseId: string, phase: object) {
      const project = this.data.projects.resources.find((p: any) => p.id === projectId)
      const phaseIndex = project.phases.findIndex((p: any) => p.id === phaseId)
      // Keep what the caller does not send (the per-day `capacity`) until the reconcile replaces it
      project.phases[phaseIndex] = { ...project.phases[phaseIndex], ...phase }
      // Local patch is already applied; reconcile derived data in the background
      this.scheduleReconcile()
    },
    async updateProjectColor(projectId: string, color: string) {
      const project = this.data.projects.resources.find((p: any) => p.id === projectId)
      project.color = color
    },
  },
})
